import React, { useState } from 'react';
import { Calendar, Award, MessageSquare, User, FileText, ChevronRight, Plus, X, Clock, MapPin } from 'lucide-react';

const ProfessionalScreen = ({ setActiveScreen }) => {
  const [user, setUser] = useState({
    name: sessionStorage.getItem("userName") || "João da Silva",
    profession: "Paisagista",
    rating: 4.8,
    completedJobs: 124,
    pendingJobs: 2
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    clientName: "",
    address: "",
    date: "",
    time: "",
    service: ""
  });

  const [nextAppointments, setNextAppointments] = useState([
    {
      id: 1,
      clientName: "Carlos Oliveira",
      address: "Rua das Flores, 123",
      date: "06/04/2025",
      time: "14:00",
      service: "Manutenção de jardim"
    },
    {
      id: 2,
      clientName: "Maria Silva",
      address: "Av. dos Ipês, 456",
      date: "08/04/2025",
      time: "10:00",
      service: "Poda de árvores"
    }
  ]);

  const menuItems = [
    { title: "Meu Perfil", icon: <User size={20} />, screen: "perfil" },
    { title: "Agenda", icon: <Calendar size={20} />, screen: "agenda" },
    { title: "Serviços", icon: <FileText size={20} />, screen: "servicos" },
    { title: "Mensagens", icon: <MessageSquare size={20} />, screen: "chat" }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAppointment({
      ...newAppointment,
      [name]: value
    });
  };

  const handleAddAppointment = (e) => {
    e.preventDefault();
    
    // Adicionar novo atendimento à lista
    const newId = nextAppointments.length > 0 ? Math.max(...nextAppointments.map(a => a.id)) + 1 : 1;
    
    const appointmentToAdd = {
      id: newId,
      ...newAppointment
    };
    
    setNextAppointments([...nextAppointments, appointmentToAdd]);
    
    // Resetar o formulário e fechar o modal
    setNewAppointment({
      clientName: "",
      address: "",
      date: "",
      time: "",
      service: ""
    });
    
    user.pendingJobs += 1;

    setShowAddModal(false);
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-20 relative">
      {/* Header */}
      <div className="p-4">
        <h1 className="text-xl font-bold">Área Profissional</h1>
      </div>

      {/* Perfil resumido */}
      <div className="bg-white p-4 shadow-sm mb-4">
        <div className="flex items-center">
          <div className="bg-teal-100 rounded-full w-16 h-16 flex items-center justify-center mr-4">
            <User size={32} className="text-teal-700" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-800">{user.name}</h2>
            <p className="text-gray-600">{user.profession}</p>
            <div className="flex items-center mt-1">
              <Award size={16} className="text-yellow-500 mr-1" />
              <span className="text-sm text-gray-700">{user.rating}</span>
              <span className="mx-2 text-gray-400">•</span>
              <span className="text-sm text-gray-700">{user.completedJobs} trabalhos</span>
            </div>
          </div>
        </div>
      </div>

      {/* Próximos agendamentos */}
      <div className="bg-white p-4 shadow-sm mb-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-gray-800">Próximos agendamentos</h3>
          <button className="text-teal-600 text-sm font-medium flex items-center" onClick={() => setActiveScreen('agenda')}>
            Ver todos
            <ChevronRight size={16} />
          </button>
        </div>

        {nextAppointments.map((appointment) => (
          <div key={appointment.id} className="border-b border-gray-100 py-3 last:border-0">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-medium text-gray-800">{appointment.clientName}</h4>
                <p className="text-sm text-gray-600">{appointment.address}</p>
                <p className="text-sm text-gray-600">{appointment.service}</p>
              </div>
              <div className="bg-teal-50 px-3 py-2 rounded-lg">
                <p className="text-sm font-medium text-teal-800">{appointment.date}</p>
                <p className="text-sm text-teal-600 text-center">{appointment.time}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Status */}
      <div className="bg-white p-4 shadow-sm mb-4">
        <h3 className="font-semibold text-gray-800 mb-3">Status de trabalhos</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-sm text-green-600">Concluídos</p>
            <p className="text-xl font-bold text-green-800">{user.completedJobs}</p>
          </div>
          <div className="bg-amber-50 p-3 rounded-lg">
            <p className="text-sm text-amber-600">Pendentes</p>
            <p className="text-xl font-bold text-amber-800">{user.pendingJobs}</p>
          </div>
        </div>
      </div>

      {/* Menu de acesso rápido */}
      <div className="bg-white p-4 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-3">Acesso rápido</h3>
        <div className="grid grid-cols-2 gap-3">
          {menuItems.map((item, index) => (
            <div 
              key={index} 
              className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
              onClick={() => setActiveScreen(item.screen)}
            >
              <div className="bg-teal-100 p-2 rounded-lg mr-3 text-teal-700">
                {item.icon}
              </div>
              <span className="font-medium text-gray-800">{item.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Botão de adicionar atendimento */}
      <button 
        className="fixed right-6 bottom-24 w-14 h-14 bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-green-700"
        onClick={() => setShowAddModal(true)}
      >
        <Plus size={24} />
      </button>

      {/* Modal de adicionar atendimento */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4">
            {/* Header do modal */}
            <div className="bg-teal-800 text-white p-4 flex items-center justify-between rounded-t-lg">
              <h2 className="text-xl font-medium">Novo Atendimento</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1">
                <X size={24} />
              </button>
            </div>

            {/* Formulário */}
            <div className="p-6">
              <form onSubmit={handleAddAppointment}>
                {/* Nome do cliente */}
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">Nome do Cliente</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="clientName"
                      value={newAppointment.clientName}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Nome do cliente"
                      required
                    />
                  </div>
                </div>

                {/* Endereço */}
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">Endereço</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="address"
                      value={newAppointment.address}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Endereço completo"
                      required
                    />
                  </div>
                </div>

                {/* Data e hora em linha */}
                <div className="mb-4 flex gap-4">
                  <div className="flex-1">
                    <label className="block text-gray-700 text-sm font-medium mb-2">Data</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Calendar size={18} className="text-gray-400" />
                      </div>
                      <input
                        type="date"
                        name="date"
                        value={newAppointment.date}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-gray-700 text-sm font-medium mb-2">Hora</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Clock size={18} className="text-gray-400" />
                      </div>
                      <input
                        type="time"
                        name="time"
                        value={newAppointment.time}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Serviço */}
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-2">Tipo de Serviço</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FileText size={18} className="text-gray-400" />
                    </div>
                    <select
                      name="service"
                      value={newAppointment.service}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500 bg-white"
                      required
                    >
                      <option value="">Selecione um serviço</option>
                      <option value="Manutenção de jardim">Manutenção de jardim</option>
                      <option value="Poda de árvores">Poda de árvores</option>
                      <option value="Plantio">Plantio</option>
                      <option value="Instalação de sistema de irrigação">Instalação de sistema de irrigação</option>
                      <option value="Paisagismo completo">Paisagismo completo</option>
                    </select>
                  </div>
                </div>

                {/* Botões de ação */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-gray-200 text-gray-800 font-medium py-3 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-teal-700 hover:bg-teal-800 text-white font-medium py-3 rounded-lg"
                  >
                    Adicionar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalScreen;